/**
 * inputDurationDirective
 *
 * Allows the user to input a duration. `value` is in seconds.
 *
 * @author Dec Norton <decnorton@gmail.com>
 */

import { isFunction } from 'lodash-es';

const KEY_UP = 38;
const KEY_DOWN = 40;

inputDurationDirective.$inject = ['$mdPanel', '$util'];

export function inputDurationDirective($mdPanel, $util) {
    return {
        //language=HTML
        template: `
            <md-input-container class="md-icon-right">
                <label>{{ ::label }}</label>

                <input type="text"
                       formatter-duration
                       name="{{ ::name || '' }}"
                       ng-model="value"
                       ng-model-options="{ debounce: 300 }"
                       ng-change="_onChange()"
                       ng-keydown="_onKeyDown($event)"
                       placeholder="0:00"
                       ng-disabled="disabled"
                       ng-required="required">

                <md-icon ng-if="!disabled"
                         ng-click="showAutocomplete($event)">
                    more_vert
                </md-icon>

                <md-icon ng-if="clearable && value && !disabled"
                         ng-click="_clear()">
                    clear
                    <md-tooltip>Clear</md-tooltip>
                </md-icon>

                <div class="hint" ng-if="hint && showHint">
                    {{ hint }}
                </div>

                <div ng-if="form && name" ng-messages="form[name].$error">
                    <div ng-message="required">Required</div>
                    <div ng-message="duration">Not a valid input</div>
                    <div ng-message="custom">{{ form[name].customError }}</div>
                    <div ng-repeat="(key, message) in errorMessages" ng-message="{{ key }}">{{ message }}</div>
                </div>
            </md-input-container>
        `,
        scope: {
            dispatchChange: '&?change',
            disabled: '=ngDisabled',
            errorMessages: '=errorMessages',
            clearable: '=?clearable',
            form: '=form',
            label: '@label',
            name: '@name',
            hint: '@?hint',
            showHint: '=?showHint',
            required: '=?required',
            value: '=value',
        },
        link: function (scope, element, attrs) {
            element.addClass('app-form-field');

            const $input = element.find('input');
            let panelRef;

            scope._onChange = _onChange;
            scope._onKeyDown = _onKeyDown;
            scope.showAutocomplete = showAutocomplete;
            scope._clear = _clear;

            // If it's undefined on the scope, it may mean that the attribute is there but
            // without content. If the attribute isn't undefined then we know it's been added.
            if (scope.required === undefined && attrs.required !== undefined) {
                scope.required = true;
            }

            if (scope.showHint === undefined) {
                scope.showHint = true;
            }

            scope.$on('$destroy', () => {
                if (panelRef) {
                    panelRef.close();
                }
            });

            function _dispatchChange() {
                if (isFunction(scope.dispatchChange)) {
                    $util.nextTickImmediate(() => {
                        scope.dispatchChange();
                    });
                }
            }

            function _onChange() {
                _dispatchChange();

                if (!scope.form || !scope.name) {
                    return;
                }

                if (scope.value) {
                    scope.form[scope.name].$setValidity('custom', true);
                    scope.form[scope.name].customError = null;
                }
            }

            function _clear() {
                scope.value = undefined;
                _onChange();
            }

            function _onKeyDown($event) {
                let handled = false;

                if (panelRef) {
                    panelRef.close();
                }

                switch ($event.keyCode) {
                    case KEY_UP:
                        _increment();
                        handled = true;
                        break;

                    case KEY_DOWN:
                        _decrement();
                        handled = false;
                        break;
                }

                if (handled) {
                    $event.preventDefault();
                }
            }

            function _increment() {
                if (scope.value !== undefined) {
                    scope.value += 60;
                    _onChange();
                }
            }

            function _decrement() {
                if (scope.value !== undefined) {
                    scope.value -= 60;
                    _onChange();
                }
            }

            function showAutocomplete($event) {
                const position = $mdPanel
                    .newPanelPosition()
                    .relativeTo($input)
                    .addPanelPosition($mdPanel.xPosition.ALIGN_START, $mdPanel.yPosition.BELOW);

                const config = {
                    attachTo: angular.element(document.body),
                    controller: PanelMenuCtrl,
                    controllerAs: 'ctrl',
                    //language=HTML
                    template: `
                        <div class="app-input-duration-panel"
                             md-whiteframe="1"
                             aria-label="Select your favorite dessert."
                             role="listbox">
                            <md-list>
                                <md-list-item ng-repeat="option in options"
                                              ng-click="selectOption(option)"
                                              ng-keydown="onKeydown($event, option)">
                                    {{ option.label }}
                                </md-list-item>
                            </md-list>
                        </div>
                    `,
                    panelClass: 'app-input-duration-panel',
                    position: position,
                    openFrom: $event,
                    clickOutsideToClose: true,
                    escapeToClose: true,
                    focusOnOpen: false,
                    onCloseSuccess: (panel, result) => {
                        if (result instanceof DurationSelectOption) {
                            scope.value = result.value;

                            _dispatchChange();
                        }
                    },
                    zIndex: 100,
                };

                $mdPanel.open(config).then(result => {
                    panelRef = result;
                });
            }
        },
    };
}

class DurationSelectOption {
    constructor(public label, public value) {
    }
}

PanelMenuCtrl.$inject = ['$scope', '$timeout', 'mdPanelRef'];

function PanelMenuCtrl($scope, $timeout, mdPanelRef) {
    $scope.options = [
        new DurationSelectOption('0:10', 600),
        new DurationSelectOption('0:15', 900),
        new DurationSelectOption('0:20', 1200),
        new DurationSelectOption('0:30', 1800),
        new DurationSelectOption('0:45', 2700),
        new DurationSelectOption('1:00', 3600),
        new DurationSelectOption('1:30', 5400),
        new DurationSelectOption('2:00', 7200),
        new DurationSelectOption('2:30', 9000),
        new DurationSelectOption('3:00', 10800),
    ];

    /**
     * @param {DurationSelectOption} option
     */
    $scope.selectOption = function (option) {
        mdPanelRef && mdPanelRef.close(option);
    };

    $scope.onKeydown = function ($event, option) {
        let handled, elements, index, prevIndex, nextIndex;

        switch ($event.which) {
            case 38: // Up Arrow.
                elements = document.querySelectorAll('.demo-menu-item');
                index = indexOf(elements, document.activeElement);
                prevIndex = (index + elements.length - 1) % elements.length;
                elements[prevIndex].focus();
                handled = true;
                break;

            case 40: // Down Arrow.
                elements = document.querySelectorAll('.demo-menu-item');
                index = indexOf(elements, document.activeElement);
                nextIndex = (index + 1) % elements.length;
                elements[nextIndex].focus();
                handled = true;
                break;

            case 13: // Enter.
            case 32: // Space.
                this.selectOption(option);
                handled = true;
                break;

            case 9: // Tab.
                mdPanelRef && mdPanelRef.close();
        }

        if (handled) {
            $event.preventDefault();
            $event.stopImmediatePropagation();
        }

        function indexOf(nodeList, element) {
            return Array.from(nodeList).findIndex(_ => _ === element);
        }
    };
}
